import { useEffect, useRef, useState } from 'react';
import { saveSession, type AuthUser } from '../auth';
import HomeTicker from '../components/HomeTicker';
import { QUARTER } from '../shared/q3';
import '../q3/q3.css';

interface LoginProps {
  onAuthed: (user: AuthUser) => void;
}

interface GoogleCredentialResponse {
  credential: string;
}

interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (resp: GoogleCredentialResponse) => void;
        ux_mode?: 'popup' | 'redirect';
      }) => void;
      prompt: (listener?: (notification: unknown) => void) => void;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

interface AppConfig {
  clientId: string;
  allowedDomain: string;
  dataUploadedAt?: string | null;
}

const UPLOADED_AT_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kolkata',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

function formatUploadedAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return UPLOADED_AT_FORMATTER.format(new Date(t)).toUpperCase();
}

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export default function Login({ onAuthed }: LoginProps) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'verifying'>('idle');
  const gsiReadyRef = useRef<boolean>(false);

  useEffect(() => {
    fetch('/api/config')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`config ${r.status}`))))
      .then((c: AppConfig) => {
        if (!c.clientId) throw new Error('Google OAuth not configured on server');
        setConfig(c);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!config) return;
    let cancelled = false;
    let timeoutId: number | null = null;

    const handleCredential = async (resp: GoogleCredentialResponse) => {
      setStatus('verifying');
      setError(null);
      try {
        const r = await fetch('/api/verify-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential: resp.credential }),
        });
        const data = (await r.json().catch(() => ({}))) as {
          token?: string;
          email?: string;
          name?: string;
          role?: 'admin' | 'rm';
          message?: string;
          error?: string;
        };
        if (!r.ok || !data.token || !data.email) {
          throw new Error(data.message || data.error || `HTTP ${r.status}`);
        }
        const user = {
          email: data.email,
          name: data.name,
          role: data.role === 'admin' ? ('admin' as const) : ('rm' as const),
        };
        saveSession(data.token, user);
        onAuthed(user);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Login failed');
        setStatus('idle');
      }
    };

    const tryInit = () => {
      if (cancelled) return true;
      const gsi = window.google?.accounts?.id;
      if (!gsi) return false;
      try {
        gsi.initialize({ client_id: config.clientId, ux_mode: 'popup', callback: handleCredential });
        gsiReadyRef.current = true;
        return true;
      } catch (e) {
        console.error('GSI init failed:', e);
        setError(e instanceof Error ? e.message : 'Google init failed');
        return true;
      }
    };

    if (tryInit()) return;
    const start = Date.now();
    const tick = () => {
      if (cancelled) return;
      if (tryInit()) return;
      if (Date.now() - start > 8000) {
        setError('Google Sign-In script did not load. Check accounts.google.com is reachable.');
        return;
      }
      timeoutId = window.setTimeout(tick, 120);
    };
    tick();
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [config, onAuthed]);

  const handleSignInClick = () => {
    const gsi = window.google?.accounts?.id;
    if (!gsi || !gsiReadyRef.current) {
      setError('Sign-in is still initializing — try again in a second.');
      return;
    }
    try {
      gsi.prompt();
    } catch (e) {
      console.error('prompt failed:', e);
      setError(e instanceof Error ? e.message : 'Could not open Google sign-in.');
    }
  };

  const uploadedLabel = formatUploadedAt(config?.dataUploadedAt);

  return (
    <div className="sk sk-login" data-view="home">
      <div className="sk-wrap">
        <div className="sk-top">
          <div className="sk-brandrow">
            <div className="sk-filogo" role="img" aria-label="FundsIndia Private Wealth" />
            <div className="sk-vr" />
            <div className="sk-toplogo sk-logo" role="img" aria-label="Shakti · Powering to 25K" />
          </div>
          {uploadedLabel && <span className="sk-login-asof">Updated {uploadedLabel}</span>}
        </div>

        <section className="sk-login-hero">
          <div className="sk-logo sk-login-mark" role="img" aria-label="Shakti · Powering to 25K" />
          <div className="sk-eyebrow">FundsIndia Private Wealth · Shakti contest series · {QUARTER.label}</div>
          <h1 className="sk-serif">
            <span className="sk-goldtext">Rise</span> to the
            <br />
            Next Level
          </h1>
          <p className="sk-sub">Two cohorts. Clear targets. One non-negotiable business mix.</p>

          <button
            type="button"
            className="sk-login-cta"
            onClick={handleSignInClick}
            disabled={status === 'verifying'}
          >
            <span className="g">
              <GoogleGlyph />
            </span>
            <span>{status === 'verifying' ? 'Verifying…' : 'Continue with Google'}</span>
            <span aria-hidden="true">→</span>
          </button>
          <div className="sk-login-meta">
            <span>
              Access restricted to <b>@{config?.allowedDomain ?? 'fundsindia.com'}</b>
            </span>
            <span>24h session · single sign-on</span>
          </div>
          {error && <div className="sk-login-error">{error}</div>}
        </section>

        <HomeTicker />
      </div>
    </div>
  );
}
